import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {FaIcon} from '../FaIcon';
import {Spinner} from '../Spinner';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {SnoozeAllAlertsModal} from './SnoozeAllAlertsModal';

// "19 de septiembre", en el idioma del usuario.
//
// ⚠️ El vencimiento lo fijó el backend en la zona horaria de QUIEN SILENCIÓ (`user.timezone()`), y
// acá se formatea en la del NAVEGADOR de quien mira. Para «una semana» da igual (son 7×24 h), pero
// «por hoy» y «este mes» vencen al cierre del día en la zona del que silenció: un compañero en otro
// huso puede leer la fecha del día siguiente. Mostrar la fecha correcta para cada lector exige que
// el backend mande la zona del perfil — hoy no viaja, así que queda anotado y no inventado.
export function formatSnoozedUntil(isoDate, language) {
    if (!isoDate) return null;
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString(language || undefined, {day: 'numeric', month: 'long'});
}

/**
 * La campanita de una línea del Tablero: silencia TODAS sus alertas por un plazo, o las reactiva.
 *
 * - Sin silenciar: el clic abre el modal con los cuatro plazos (card ND0fME30 — antes silenciaba
 *   "por hoy" sin preguntar, y los clientes que querían apagar una línea volvían cada mañana).
 * - Silenciada: la campana se pinta activa y el tooltip dice HASTA CUÁNDO; el clic reactiva de una,
 *   que es la dirección segura.
 *
 * `isSnoozed` en `null` es "todavía no sé" (la fila no cargó) y se muestra como carga. `snoozedUntil`
 * es el ISO del vencimiento o `null` = sin vencimiento; lo trae la fila del tablero
 * (`alerts_snooze`) y, recién silenciada, la respuesta del POST.
 */
export function SnoozeAllAlertsButton(props) {
    const {businessUnitId, businessUnitName, clientName, isSnoozed, snoozedUntil, onSnooze, onStopSnooze} = props;
    const {t, i18n} = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const endpoint = `${API_URL}business-unit/${businessUnitId}/alert-snooze-all`;

    const snooze = (period) => {
        setIsSaving(true);
        new APIClient(endpoint).call('POST', {period})
            .then((response) => {
                // `!== 'OK'`: el backend contesta el código tipado en `status` (ERROR_INVALID_PERIOD,
                // NETWORK_ERROR…); compararlo contra 'ERROR' se los comería en silencio.
                if (!response || response.status !== 'OK') {
                    toast.error(t('dashboard.snooze_all.error'));
                    return;
                }
                setIsModalOpen(false);
                onSnooze(response.data || {});
            })
            .finally(() => setIsSaving(false));
    };

    const stopSnooze = () => {
        if (isSaving) return;   // dos clics rápidos mandaban dos DELETE
        setIsSaving(true);
        new APIClient(endpoint).call('DELETE')
            .then((response) => {
                if (!response || response.status !== 'OK') {
                    toast.error(t('dashboard.snooze_all.unsnooze_error'));
                    return;
                }
                onStopSnooze();
            })
            .finally(() => setIsSaving(false));
    };

    const tooltipText = () => {
        if (!isSnoozed) return t('dashboard.snooze_all.tooltip_snooze');
        const until = formatSnoozedUntil(snoozedUntil, i18n.language);
        return until
            ? t('dashboard.snooze_all.tooltip_snoozed_until', {date: until})
            : t('dashboard.snooze_all.tooltip_snoozed_forever');
    };

    const renderTooltip = () => (
        <div className="bu-tooltip">
            <FaIcon iconName={isSnoozed ? 'bell' : 'bell-slash'}/>
            <p className="tooltip-description">{tooltipText()}</p>
        </div>
    );

    // `isSaving` NO desmonta nada: mientras guarda, el spinner reemplaza al ícono DENTRO del botón.
    // Sacar el botón de la pantalla desmontaba con él al modal, que perdía su `useState` — y tras un
    // error el plazo elegido volvía a «Una semana» sin que nada lo avisara (QA-campanita-08).
    if (isSnoozed === null || isSnoozed === undefined) {
        return (
            <div>
                <div className="bu-cell-button">
                    <Spinner color="positive very-small"/>
                </div>
            </div>
        );
    }

    return (
        <div>
            <button type="button"
                    className={`bu-cell-button snooze${isSnoozed ? ' -on' : ''}`}
                    aria-label={tooltipText()}
                    disabled={isSaving}
                    onClick={isSnoozed ? stopSnooze : () => setIsModalOpen(true)}>
                {/* El ícono acompaña al estado, como el tooltip: campana tachada = «silenciar»,
                    campana entera = «están silenciadas, clic para reactivarlas». */}
                {isSaving
                    ? <Spinner color="positive very-small"/>
                    : <FaIcon iconName={isSnoozed ? 'bell' : 'bell-slash'}/>}
                {renderTooltip()}
            </button>
            {isModalOpen && (
                <SnoozeAllAlertsModal lineName={`${clientName} | ${businessUnitName}`}
                                      isSaving={isSaving}
                                      onConfirm={snooze}
                                      onCancel={() => setIsModalOpen(false)}/>
            )}
        </div>
    );
}

export default SnoozeAllAlertsButton;
